import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api.ts';
import type { AuthUser } from '../types.ts';
import { dashboardPathFor } from './dashboardPath.ts';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (input: { email: string; password: string; firstName: string; lastName: string; role: 'EXTRA' | 'ORG_OWNER'; referralCode?: string }) => Promise<AuthUser>;
  activate: (token: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  /** Le Super Admin agit en tant que ce compte : recharge la session, puis redirige vers son espace. */
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ user: AuthUser }>('/auth/me')
      .then((res) => setUser(res.user))
      .catch((err) => {
        if (!(err instanceof ApiError && err.status === 401)) throw err;
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const res = await api.post<{ user: AuthUser }>('/auth/login', { email, password });
    setUser(res.user);
    return res.user;
  };

  const register: AuthContextValue['register'] = async (input) => {
    const res = await api.post<{ user: AuthUser }>('/auth/register', input);
    setUser(res.user);
    return res.user;
  };

  const activate = async (token: string, password: string) => {
    const res = await api.post<{ user: AuthUser }>('/auth/activate', { token, password });
    setUser(res.user);
    return res.user;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
  };

  const startImpersonation = async (userId: string) => {
    await api.post(`/admin/users/${userId}/impersonate`);
    // Rechargement complet : toutes les données déjà chargées sont celles du Super Admin.
    const me = await api.get<{ user: AuthUser }>('/auth/me');
    window.location.assign(dashboardPathFor(me.user.role));
  };

  const stopImpersonation = async () => {
    await api.post('/auth/impersonation/stop');
    window.location.assign('/admin/users');
  };

  return <AuthContext.Provider value={{ user, loading, login, register, activate, logout, startImpersonation, stopImpersonation }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
